import { InjectionToken } from '@angular/core';
import { AppConfigEffects, fromAppConfig } from '@navfleet/client/app-config/domain/data-access';
import { fromAuthentication } from '@navfleet/client/authentication/domain/data-access';
import { AuthenticationEffects } from '@navfleet/client/authentication/effects';
import { FleetEffects } from '@navfleet/client/fleet/domain/data-access';
import { fromFleet } from '@navfleet/client/fleet/domain/data-access';
import { fromLicense } from '@navfleet/client/license/domain/data-access';
import { VesselPassageEffects, VesselScheduleEffects } from '@navfleet/client/monitoring/state/effects';
import { VesselDestinationEffects, fromVesselDestination } from '@navfleet/client/monitoring/state/vessel-destination';
import { VesselEventEffects, fromVesselEvent } from '@navfleet/client/monitoring/state/vessel-event';
import { VesselNavStatusEffects, fromVesselNavStatus } from '@navfleet/client/monitoring/state/vessel-nav-status';
import { fromVesselPassage } from '@navfleet/client/monitoring/state/vessel-passage';
import {
    VesselPassagePerformanceEffects,
    fromVesselPassagePerformance,
} from '@navfleet/client/monitoring/state/vessel-passage-performance';
import { VesselPassagePlanEffects, fromVesselPassagePlan } from '@navfleet/client/monitoring/state/vessel-passage-plan';
import { VesselRouteEffects, fromVesselRoute } from '@navfleet/client/monitoring/state/vessel-route';
import { fromVesselSchedule } from '@navfleet/client/monitoring/state/vessel-schedule';
import { VesselWeatherEffects, fromVesselWeather } from '@navfleet/client/monitoring/state/vessel-weather';
import { fromPermission } from '@navfleet/client/permission/domain/data-access';
import { PortEffects, fromPort } from '@navfleet/client/port/domain/data-access';
import { TenantEffects, fromTenant } from '@navfleet/client/tenant/domain/data-access';
import { UserPrivilegeEffects, fromUserPrivilege } from '@navfleet/client/user/privilege/domain/data-access';
import { VesselEffects, fromVessel } from '@navfleet/client/vessel/domain/data-access';
import { VesselTrackEffects, fromVesselTrack } from '@navfleet/client/vessel/track/domain/data-access';
import { Action, ActionReducerMap } from '@ngrx/store';
import { RootEffects } from './root.effects';

export interface State {
    [fromAppConfig.APP_CONFIG_FEATURE_KEY]: fromAppConfig.AppConfigState;
    [fromAuthentication.AUTHENTICATION_STATE_FEATURE_KEY]: fromAuthentication.AuthenticationState;
    [fromFleet.FLEET_FEATURE_KEY]: fromFleet.FleetState;
    [fromLicense.LICENSE_FEATURE_KEY]: fromLicense.LicenseState;
    [fromPermission.PERMISSION_FEATURE_KEY]: fromPermission.PermissionState;
    [fromPort.FEATURE_KEY]: fromPort.PortState;
    [fromTenant.TENANT_STATE_FEATURE_KEY]: fromTenant.TenantState;
    [fromVessel.VESSEL_FEATURE_KEY]: fromVessel.State;
    [fromVesselDestination.VESSEL_DESTINATION_FEATURE_KEY]: fromVesselDestination.VesselDestinationState;
    [fromVesselEvent.VESSEL_EVENT_FEATURE_KEY]: fromVesselEvent.VesselEventState;
    [fromVesselNavStatus.VESSEL_NAV_STATUS_FEATURE_KEY]: fromVesselNavStatus.VesselNavStatusState;
    [fromVesselPassage.VESSEL_PASSAGE_FEATURE_KEY]: fromVesselPassage.VesselPassageState;
    [fromVesselPassagePerformance.VESSEL_PASSAGE_PERFORMANCE_FEATURE_KEY]: fromVesselPassagePerformance.VesselPassagePerformanceState;
    [fromVesselPassagePlan.VESSEL_PASSAGE_PLAN_FEATURE_KEY]: fromVesselPassagePlan.VesselPassagePlanState;
    [fromVesselRoute.VESSEL_ROUTE_FEATURE_KEY]: fromVesselRoute.VesselRouteState;
    [fromVesselSchedule.VESSEL_SCHEDULE_FEATURE_KEY]: fromVesselSchedule.VesselScheduleState;
    [fromVesselTrack.VESSEL_TRACK_FEATURE_KEY]: fromVesselTrack.VesselTrackState;
    [fromVesselWeather.FEATURE_KEY]: fromVesselWeather.VesselWeatherState;
    [fromUserPrivilege.FEATURE_KEY]: fromUserPrivilege.UserPrivilegeState;
}

export const ROOT_REDUCERS = new InjectionToken<ActionReducerMap<State, Action>>('Root reducers token', {
    factory: (): ActionReducerMap<State, Action> => ({
        [fromAppConfig.APP_CONFIG_FEATURE_KEY]: fromAppConfig.reducer,
        [fromAuthentication.AUTHENTICATION_STATE_FEATURE_KEY]: fromAuthentication.reducer,
        [fromFleet.FLEET_FEATURE_KEY]: fromFleet.fleetStateReducer,
        [fromLicense.LICENSE_FEATURE_KEY]: fromLicense.reducer,
        [fromPermission.PERMISSION_FEATURE_KEY]: fromPermission.reducer,
        [fromPort.FEATURE_KEY]: fromPort.reducer,
        [fromTenant.TENANT_STATE_FEATURE_KEY]: fromTenant.reducer,
        [fromVessel.VESSEL_FEATURE_KEY]: fromVessel.reducer,
        [fromVesselDestination.VESSEL_DESTINATION_FEATURE_KEY]: fromVesselDestination.getDestinationReducer,
        [fromVesselEvent.VESSEL_EVENT_FEATURE_KEY]: fromVesselEvent.getVesselEventReducer,
        [fromVesselNavStatus.VESSEL_NAV_STATUS_FEATURE_KEY]: fromVesselNavStatus.reducer,
        [fromVesselPassage.VESSEL_PASSAGE_FEATURE_KEY]: fromVesselPassage.getPassageReducer,
        [fromVesselPassagePerformance.VESSEL_PASSAGE_PERFORMANCE_FEATURE_KEY]: fromVesselPassagePerformance.reducer,
        [fromVesselPassagePlan.VESSEL_PASSAGE_PLAN_FEATURE_KEY]: fromVesselPassagePlan.getPassagePlanReducer,
        [fromVesselRoute.VESSEL_ROUTE_FEATURE_KEY]: fromVesselRoute.getRouteReducer,
        [fromVesselSchedule.VESSEL_SCHEDULE_FEATURE_KEY]: fromVesselSchedule.reducer,
        [fromVesselTrack.VESSEL_TRACK_FEATURE_KEY]: fromVesselTrack.reducer,
        [fromVesselWeather.FEATURE_KEY]: fromVesselWeather.reducer,
        [fromUserPrivilege.FEATURE_KEY]: fromUserPrivilege.reducer,
    }),
});

export const ROOT_EFFECTS = [
    RootEffects,
    AppConfigEffects,
    AuthenticationEffects,
    VesselEffects,
    VesselTrackEffects,
    TenantEffects,
    FleetEffects,
    VesselDestinationEffects,
    VesselPassageEffects,
    VesselPassagePerformanceEffects,
    VesselNavStatusEffects,
    VesselRouteEffects,
    VesselScheduleEffects,
    VesselPassagePlanEffects,
    VesselEventEffects,
    VesselWeatherEffects,
    PortEffects,
    UserPrivilegeEffects,
];
